/**
 * 抽查执行页（抽屉）
 *
 * 抽查人在此逐单看 POD 判定「符合 / 可拍门牌」，全部判完后手选任务级结论。
 *
 * POD 以缩略图卡片网格呈现而非表格：抽查人的动作本质是「看图判断」，
 * 图必须是主体；做成表格会让每单都要点开看，操作效率差一个量级。
 *
 * 抽查人不预先分配、无领取动作 —— 谁提交结论就记谁。并发保护靠后端提交时的
 * 状态 CAS：两人同时查同一任务，后提交者被拒。
 */
import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Button, Drawer, Empty, Image, Input, Tag, message} from 'antd';
import {useTranslation} from 'react-i18next';
import {
    ExemptTargetType,
    SampleJudgeResult,
    SpotSampleRow,
    SpotTaskConclusion,
    SpotTaskDetail,
    SpotTaskStatus,
} from '../types/ExemptSpotCheckType';
import {EMPTY_METRIC, formatCount} from '../utils/metricFormat';
import {CONCLUSION_TAG_COLOR, getAvailableConclusions, isOfflineConclusion} from '../config/exemptSpotCheckConfig';
import {useSpotTaskActions} from '../hooks/useSpotTaskActions';
import {POD_EXEMPT_PREVIEW_ROOT_CLASS} from '../../../../components/podExempt/podExemptConstants';

/**
 * 「邮编 / 范围」：单地址只显示邮编，围栏追加地址数。
 *
 * 围栏地址数为空表示主 R 的物化挂载尚未完成，展示「统计中」而非 0 ——
 * 0 会被误读为「这个围栏一个地址都没有」。
 */
const formatZipcodeScope = (task: SpotTaskDetail['task']): string => {
    const zipcode = task.zipcode ?? EMPTY_METRIC;
    if (task.targetType !== ExemptTargetType.FENCE) {
        return zipcode;
    }
    return `${zipcode} · ${task.addressCount ?? EMPTY_METRIC}`;
};

interface SpotTaskDetailDrawerProps {
    visible: boolean;
    detail?: SpotTaskDetail;
    onClose: () => void;
    onConcluded: () => void;
}

const SpotTaskDetailDrawer: React.FC<SpotTaskDetailDrawerProps> = ({
    visible,
    detail,
    onClose,
    onConcluded,
}) => {
    const {t} = useTranslation();
    const {loading, judgeSample, concludeTask} = useSpotTaskActions();
    // 全量字典（含停用）：样本固化的是抽样当时的原因码，字典停用后仍要能回显
    const [samples, setSamples] = useState<SpotSampleRow[]>([]);
    const [conclusion, setConclusion] = useState<SpotTaskConclusion | undefined>(undefined);
    const [remark, setRemark] = useState('');

    useEffect(() => {
        setSamples(detail?.samples ?? []);
        setConclusion(detail?.task.conclusion);
        setRemark(detail?.task.conclusionRemark ?? '');
    }, [detail]);

    /** 已完成的任务只读展示，不可再判定 */
    const readOnly = detail?.task.status === SpotTaskStatus.DONE;

    const judgedCount = useMemo(
        () => samples.filter((item) => item.judgeResult !== SampleJudgeResult.PENDING).length,
        [samples],
    );

    const failCount = useMemo(
        () => samples.filter((item) => item.judgeResult === SampleJudgeResult.FAIL).length,
        [samples],
    );


    /**
     * 信息字段按原型顺序固定六项，左右两列交替排布。
     *
     * 任务来源的值含 Tag + 入池原因，故为 ReactNode 而非纯字符串。
     */
    const infoFields = useMemo<
        Array<{label: string; value: React.ReactNode; fullWidth?: boolean}>
    >(() => {
        if (!detail) {
            return [];
        }
        const task = detail.task;
        return [
            {label: t('exemptSpotCheck.detail.fields.address'), value: task.address},
            {
                label: t('exemptSpotCheck.detail.fields.zipcodeScope'),
                value: formatZipcodeScope(task),
            },
            {
                label: t('exemptSpotCheck.detail.fields.targetNo'),
                value: <span className="text-purple-600">{task.targetNo}</span>,
            },
            {
                label: t('exemptSpotCheck.task.columns.source'),
                // 入池原因是整句描述，比其余字段长数倍；占整行避免挤成三四行而右侧留白
                fullWidth: true,
                value: (
                    <span>
                        <Tag>{t(`exemptSpotCheck.taskSource.${task.source}`)}</Tag>
                        <span className="text-gray-500">{task.sourceDetail}</span>
                    </span>
                ),
            },
            {
                label: t('exemptSpotCheck.task.columns.exemptCount'),
                value: formatCount(task.exemptCount, String(t('exemptSpotCheck.unit.order'))),
            },
            // 抽查人在提交结论时才记录，待抽查的单必然没有，整项不展示（与列表页口径一致）
            ...(task.assigneeName
                ? [{label: t('exemptSpotCheck.task.columns.assignee'), value: task.assigneeName}]
                : []),
        ];
    }, [detail, t]);

    const handleJudge = async (row: SpotSampleRow, result: SampleJudgeResult) => {
        const ok = await judgeSample(row.id, result, row.judgeRemark);
        if (!ok) {
            return;
        }
        setSamples((prev) =>
            prev.map((item) => (item.id === row.id ? {...item, judgeResult: result} : item)),
        );
    };

    /**
     * 提交条件：选了结论 + 判不通过时必须写说明。
     *
     * 「不通过必填说明」是原型的硬约束：不通过会导致地址永久下线并处罚 DSP，
     * 说明是 DSP 申诉时唯一能看到的依据，空着等于无法申诉。
     *
     * 样本不构成提交前置条件 —— 样本是判定参考，抽查人看完 POD 即可下结论，
     * 未逐单点完或取样为空都不拦截。
     */
    const submitDisabledReason = useMemo((): string | undefined => {
        if (!conclusion) {
            return String(t('exemptSpotCheck.detail.selectConclusionWarning'));
        }
        if (isOfflineConclusion(conclusion) && !remark.trim()) {
            return String(t('exemptSpotCheck.detail.remarkRequiredWarning'));
        }
        return undefined;
    }, [conclusion, remark, t]);

    const handleConclude = async () => {
        if (!detail) {
            return;
        }
        if (submitDisabledReason) {
            message.warning(submitDisabledReason);
            return;
        }
        const ok = await concludeTask(detail.task.id, conclusion!, remark);
        if (ok) {
            onConcluded();
        }
    };

    /**
     * POD 卡片：图为主体，判定按钮紧贴图下方。
     *
     * 卡片边框随判定结果变色（可拍门牌=红、符合=绿、未判=灰），
     * 使抽查人扫一眼即知还剩哪几张没判，不必逐张看按钮状态。
     */
    const renderSampleCard = (row: SpotSampleRow) => {
        const judged = row.judgeResult !== SampleJudgeResult.PENDING;
        const borderClass =
            row.judgeResult === SampleJudgeResult.FAIL
                ? 'border-red-200'
                : row.judgeResult === SampleJudgeResult.PASS
                  ? 'border-green-200'
                  : 'border-gray-200';
        return (
            <div
                key={row.id}
                className={`overflow-hidden rounded-md border bg-white ${borderClass}`}
            >
                {row.podImageUrls.length > 0 ? (
                    // 封面显示首图，点开进 PreviewGroup 可左右翻看该单全部 POD；
                    // 折叠态只下载封面，与终审页画廊同一套交互
                    <div className="relative">
                        <Image.PreviewGroup
                            items={row.podImageUrls}
                            preview={{rootClassName: POD_EXEMPT_PREVIEW_ROOT_CLASS}}
                        >
                            <Image
                                src={row.podImageUrls[0]}
                                alt={row.waybillNo}
                                width="100%"
                                height={180}
                                style={{objectFit: 'cover', display: 'block'}}
                                loading="lazy"
                            />
                        </Image.PreviewGroup>
                        {row.podImageUrls.length > 1 && (
                            <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-black/55 px-1.5 py-0.5 text-xs leading-none text-white">
                                {row.podImageUrls.length}
                            </span>
                        )}
                    </div>
                ) : (
                    // POD 取不到时不阻断判定，抽查人可据运单号手动核查
                    <div className="flex h-[180px] w-full items-center justify-center bg-gray-100 text-xs text-gray-400">
                        {t('exemptSpotCheck.detail.podUnavailable')}
                    </div>
                )}

                <div className="flex flex-col gap-2 px-3 py-2.5">
                    <div className="text-xs leading-normal">
                        {/* 运单号是展示快照，可能取不到；此时回落订单 ID，保证卡片始终可标识 */}
                        <div className="text-gray-800">{row.waybillNo ?? `#${row.orderId}`}</div>
                        <div className="text-gray-500">
                            {[row.driverName, row.deliveredAt ? new Date(row.deliveredAt).toLocaleString() : undefined]
                                .filter(Boolean)
                                .join(' · ')}
                        </div>
                    </div>

                    {/*
                        查看与审核共用同一组按钮：只读时禁用并把未选中项置灰。
                        查看态换成标签会让两个场景的卡片高度与形态都不一致，回看时难以对照。
                    */}
                    <div className="flex gap-1.5">
                        {[SampleJudgeResult.PASS, SampleJudgeResult.FAIL].map((result) => {
                            const active = judged && row.judgeResult === result;
                            const disabled = readOnly || loading;
                            return (
                                <button
                                    key={result}
                                    type="button"
                                    // 判定是即时落库的写操作，提交中禁用防连点重复请求
                                    disabled={disabled}
                                    onClick={() => handleJudge(row, result)}
                                    className={`flex-1 rounded border px-2 py-1 text-xs ${
                                        disabled ? 'cursor-default' : 'cursor-pointer'
                                    } ${loading ? 'opacity-60' : ''} ${
                                        active
                                            ? 'border-purple-600 bg-purple-600 text-white'
                                            : readOnly
                                              ? 'border-gray-200 bg-gray-50 text-gray-300'
                                              : 'border-gray-300 bg-white text-gray-700'
                                    }`}
                                >
                                    {t(`exemptSpotCheck.judgeResult.${result}`)}
                                </button>
                            );
                        })}
                    </div>

                    {readOnly
                        ? row.judgeRemark && (
                              <div className="text-xs text-gray-500">{row.judgeRemark}</div>
                          )
                        : null}
                </div>
            </div>
        );
    };

    return (
        <Drawer
            title={
                detail ? (
                    <span className="flex items-center gap-2">
                        <span>{`${detail.task.taskNo} · ${detail.task.address}`}</span>
                        {/* 结论标签贴标题：回看时一眼知道这单判没判、判的什么，不必滚到底部 */}
                        {detail.task.conclusion && (
                            <Tag color={CONCLUSION_TAG_COLOR[detail.task.conclusion]}>
                                {t(`exemptSpotCheck.conclusion.${detail.task.conclusion}`)}
                            </Tag>
                        )}
                    </span>
                ) : (
                    t('exemptSpotCheck.detail.title')
                )
            }
            width={1000}
            open={visible}
            onClose={onClose}
            destroyOnClose
        >
            {detail && (
                <div className="flex flex-col gap-[22px]">
                    {/*
                        信息区：两列自适应网格，每行「标签 + 值」左右排布并以细横线分隔。
                        标签定宽 92px 保证多行标签左对齐；容器窄于 ~600px 时自动降为单列。
                        不用灰底卡片 —— 分隔线比色块更轻，信息密度高时不会显得笨重。
                    */}
                    <div
                        className="grid gap-x-8 text-[13px]"
                        style={{gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'}}
                    >
                        {infoFields.map((field) => (
                            <div
                                key={field.label}
                                className="flex min-w-0 items-baseline gap-3 border-b border-gray-100 py-2.5"
                                style={field.fullWidth ? {gridColumn: '1 / -1'} : undefined}
                            >
                                <span className="shrink-0 basis-[92px] text-gray-500">
                                    {field.label}
                                </span>
                                <span className="min-w-0 flex-1 break-words leading-normal text-gray-800">
                                    {field.value}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <span className="text-sm font-semibold">
                            {t('exemptSpotCheck.detail.sampleHint')}
                        </span>
                        <span className="text-xs text-gray-500">
                            {t('exemptSpotCheck.detail.sampleHintDesc', {total: samples.length})}
                        </span>
                        <span className="ml-auto text-xs text-gray-600">
                            {t('exemptSpotCheck.detail.judgedProgress', {
                                judged: judgedCount,
                                total: samples.length,
                            })}
                        </span>
                    </div>

                    {samples.length === 0 ? (
                        <Empty description={t('exemptSpotCheck.detail.noSample')} />
                    ) : (
                        <div
                            className="grid gap-3"
                            style={{gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))'}}
                        >
                            {samples.map(renderSampleCard)}
                        </div>
                    )}

                    {readOnly ? (
                        // 已完成：结论与说明是处罚依据链的一环，回看时必须能看到当初判了什么、为什么
                        <div className="flex flex-col gap-2.5">
                            <div className="text-sm font-semibold">
                                {t('exemptSpotCheck.detail.conclusionTitle')}
                            </div>
                            <div className="flex items-center gap-2">
                                {detail.task.conclusion && (
                                    <Tag color={CONCLUSION_TAG_COLOR[detail.task.conclusion]}>
                                        {t(`exemptSpotCheck.conclusion.${detail.task.conclusion}`)}
                                    </Tag>
                                )}
                                {detail.task.doneAt && (
                                    <span className="text-xs text-gray-500">
                                        {new Date(detail.task.doneAt).toLocaleString()}
                                    </span>
                                )}
                            </div>
                            {detail.task.conclusionRemark && (
                                <div className="whitespace-pre-wrap rounded bg-gray-50 p-3 text-[13px] leading-relaxed text-gray-700">
                                    {detail.task.conclusionRemark}
                                </div>
                            )}
                            <div className="mt-2 flex items-center justify-between gap-4 border-t border-gray-200 pt-3.5">
                                <span className="text-xs text-gray-500">
                                    {t('exemptSpotCheck.detail.sampleSummary', {
                                        judged: judgedCount,
                                        total: samples.length,
                                        fail: failCount,
                                    })}
                                </span>
                                <Button onClick={onClose}>{t('exemptSpotCheck.detail.cancel')}</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2.5">
                            <div className="text-sm font-semibold">
                                {t('exemptSpotCheck.detail.conclusionTitle')}
                            </div>

                            {/*
                                结论用按钮组而非单选列表：只有三个互斥选项，
                                按钮组一眼可见当前选中，且与判定按钮（符合/可拍门牌）视觉一致。
                                纯手选，系统不预选也不自动判定。
                            */}
                            <div className="flex flex-wrap gap-2">
                                {getAvailableConclusions().map((item) => (
                                    <Button
                                        key={item}
                                        type={conclusion === item ? 'primary' : 'default'}
                                        ghost={conclusion === item}
                                        onClick={() => setConclusion(item)}
                                    >
                                        {t(`exemptSpotCheck.conclusion.${item}`)}
                                    </Button>
                                ))}
                            </div>

                            <Input.TextArea
                                rows={4}
                                value={remark}
                                maxLength={1000}
                                /* 提示恒含「不通过必填」，不随结论切换 —— 使用者选之前就该知道规则 */
                                placeholder={String(
                                    t('exemptSpotCheck.detail.conclusionRemarkPlaceholder'),
                                )}
                                onChange={(e) => setRemark(e.target.value)}
                            />

                            {isOfflineConclusion(conclusion) && (
                                <Alert
                                    type={conclusion === SpotTaskConclusion.OFFLINE_BLOCKED ? 'error' : 'warning'}
                                    showIcon
                                    message={t(
                                        conclusion === SpotTaskConclusion.OFFLINE_BLOCKED
                                            ? 'exemptSpotCheck.detail.offlineBlockedWarning'
                                            : 'exemptSpotCheck.detail.offlineWarning',
                                    )}
                                />
                            )}

                            {/*
                                底部操作条：左侧汇总、右侧按钮，与上方内容以分隔线区隔。
                                汇总放在提交按钮同一行 —— 使用者提交前最后一眼看到的
                                就是「判了几单、几单可拍门牌」，这正是结论的依据。
                            */}
                            <div className="mt-2 flex items-center justify-between gap-4 border-t border-gray-200 pt-3.5">
                                <span className="text-xs text-gray-500">
                                    {t('exemptSpotCheck.detail.sampleSummary', {
                                        judged: judgedCount,
                                        total: samples.length,
                                        fail: failCount,
                                    })}
                                </span>
                                <div className="flex gap-2.5">
                                    <Button onClick={onClose}>
                                        {t('exemptSpotCheck.detail.cancel')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        loading={loading}
                                        disabled={Boolean(submitDisabledReason)}
                                        onClick={handleConclude}
                                    >
                                        {t('exemptSpotCheck.detail.submitConclusion')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default SpotTaskDetailDrawer;
